import React, { ReactNode, useEffect, useState } from 'react';
import 'bootstrap-select';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import { useSelector } from 'react-redux';
import { SupplyMonitorResp } from 'domain/payload/response/clientTransmora/storeDirectorySupplyMonitor/SupplyMonitorResp';
import StoreDirectorySupplyMonitorService from 'service/ClientTransmoraService/StoreDirectorySupplyMonitorService';
import { StoreDirectorySupplyMonitorReq } from 'domain/payload/request/clientTransmora/storeDirectorySupplyMonitor/StoreDirectorySupplyMonitorReq';
import { MonitorProgressTrackerResultResp } from 'domain/payload/response/monitor/MonitorProgressTrackerResp';
import ModalFilterSupplyMonitor from 'component/ModalFilterSupplyMonitor';
import AreaMerchantPartnerListModal from 'component/supplyMonitorModal/AreaMerchantPartnerListModal';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';

interface PlaceholderProps {
  height?: number;
  children?: ReactNode;
}

function Placeholder({ height = 180, children }: PlaceholderProps) {
  return (
    <div
      className="p-3 border rounded w-100"
      style={{ height: height, background: '#ffffff' }}
    >
      {children}
    </div>
  );
}

function LoadingOverlay() {
  return (
    <div
      className="top-0 position-absolute start-0 w-100 h-100 d-flex justify-content-center align-items-center"
      style={{ background: 'rgba(255, 255, 255, 0.6)', zIndex: 10 }}
    >
      <div
        className="spinner-border text-primary"
        role="status"
        style={{ width: 60, height: 60 }}
      >
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function StatCard({
  label,
  main,
  sub,
  note,
}: {
  label: string;
  main: string;
  sub?: string;
  note?: string;
}) {
  return (
    <div className="col">
      <div className="border rounded bg-white p-3 h-100">
        <div className="text-uppercase text-muted fw-semibold pb-2" style={{ fontSize: 12 }}>
          {label}
        </div>
        <div className="mt-1 fw-bold" style={{ fontSize: 30 }}>
          {main}
          {sub && (
            <span style={{ fontSize: 16, color: "#6B7280" }}>
              {" "}/ {sub}
            </span>
          )}
        </div>
        {note && (
          <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
            {note}
          </div>
        )}
      </div>
    </div>
  );
}

function TrendChart({
  label,
  data,
  color,
  fillColor,
}: {
  label: string;
  data: MonitorProgressTrackerResultResp[];
  color: string;
  fillColor: string;
}) {
  const labels = data.map((d) => d.label);
  const values = data.map((d) => d.value);

  const chartData = {
    labels,
    datasets: [
      {
        label,
        data: values,
        borderColor: color,
        backgroundColor: fillColor,
        fill: true,
        tension: 0.2,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 1.5,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const, align: "end" as const },
    },
    scales: {
      y: { grid: { color: "#F3F4F6" } },
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 5,
          maxRotation: 45,
          minRotation: 45
        }
      },
    },
  };

  return <Line data={chartData} options={options} />;
}

function AcquiredStoresChart({ data }: { data: MonitorProgressTrackerResultResp[] }) {
  const labels = data.map((d) => d.label);
  const values = data.map((d) => d.value);

  const chartData = {
    labels,
    datasets: [
      {
        label: "Acquired Stores",
        data: values,
        borderColor: "#2563EB",
        backgroundColor: "rgba(37,99,235,0.06)",
        fill: true,
        tension: 0.2,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 1.5,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const, align: "end" as const },
      title: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: "#F3F4F6" }
      },
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 8,
          maxRotation: 45,
          minRotation: 45
        }
      },
    },
  };

  return <Line data={chartData} options={options} />;
}

function StreetTypeDoughnut({ items }: { items: MonitorProgressTrackerResultResp[] }) {
  const total = items.reduce((s, i) => s + i.value, 0);

  const chartData = {
    labels: items.map((i) => i.label),
    datasets: [
      {
        data: items.map((i) => i.value),
        backgroundColor: items.map((i) => i.color),
        borderWidth: 2,
        borderColor: "#fff",
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%",
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
  };

  return (
    <div className="d-flex align-items-center gap-4 w-100 h-100">
      {/* Doughnut */}
      <div style={{ width: 180, height: 180, flexShrink: 0, position: "relative" }}>
        <Doughnut data={chartData} options={options} />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <div style={{ fontSize: 10, color: "#6B7280", fontWeight: 600, textTransform: "uppercase" }}>
            BREAKDOWN
          </div>
          <div style={{ fontSize: 10, color: "#6B7280", fontWeight: 600, textTransform: "uppercase" }}>
            STREE TYPE
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="d-flex flex-column gap-2">
        {items.map((item) => {
          const pct = total ? Math.round((item.value / total) * 100) : 0;
          return (
            <div key={item.label} className="d-flex align-items-center gap-2">
              <div
                style={{ width: 12, height: 12, borderRadius: 2, background: item.color, flexShrink: 0 }}
              />
              <span style={{ fontSize: 13 }}>
                {item.label}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {item.value.toLocaleString()}
              </span>
              <span style={{ fontSize: 12, color: "#6B7280" }}>({pct}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PointInterestBar({ items }: { items: MonitorProgressTrackerResultResp[] }) {
  const chartData = {
    labels: items.map((i) => i.label),
    datasets: [
      {
        label: "Actual Achievement",
        data: items.map((i) => i.value),
        backgroundColor: "#2563EB",
        borderRadius: 3,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const, align: "end" as const },
      title: { display: false },
    },
    scales: {
      y: { beginAtZero: true, grid: { color: "#F3F4F6" } },
      x: { grid: { display: false } },
    },
  };

  return <Bar data={chartData} options={options} />;
}

const StoreDirectorySupplyMonitor: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const token = profile?.data?.token;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);

  const [activeFilters, setActiveFilters] = useState<{
    startDate?: string | null;
    endDate?: string | null;
    provinceId?: string[];
    cityId?: string[];
    districtId?: string[];
    subdistrictId?: string[];
    roadTypeId?: string[];
    poiCategory?: string[];
  }>({
    startDate: null,
    endDate: null,
    provinceId: [] as string[],
    cityId: [] as string[],
    districtId: [] as string[],
    subdistrictId: [] as string[],
    roadTypeId: [] as string[],
    poiCategory: [] as string[]
  });

  // ── State ──
  const [loading, setLoading] = useState(false);
  const [loadingArea, setLoadingArea] = useState(false);
  const [data, setData] = useState<SupplyMonitorResp | null>(null);
  const [areaList, setAreaList] = useState<any[]>([]);

  // Filter modal (UI stub — wire to real filter when ready)
  const [openFilter, setOpenFilter] = useState(false);

  // List by areas
  const [areaGroupBy, setAreaGroupBy] = useState<string>("subdistrict");
  const [balanceFilter, setBalanceFilter] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("province");
  const [sortOrder, setSortOrder] = useState<string>("asc");
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[] | null>(null);
  const [selectedRepresentativeAreaId, setSelectedRepresentativeAreaId] = useState("");

  const AREA_GROUP_BY_OPTIONS = [
    { id: 'subdistrict', name: "By Subdistrict" },
    { id: 'district', name: "By District" },
    { id: 'city', name: "By City" },
    { id: 'province', name: "By Province" },
  ];
  const BALANCE_FILTER_OPTION = [
    { id: '', name: "All" },
    { id: 'deficit', name: "Defisit" },
    { id: 'surplus', name: "Surplus" },
    { id: 'balanced', name: "Balanced" },
  ];

  const supplyMonitorService = StoreDirectorySupplyMonitorService()

  const fetchData = async () => {
    const token = profile?.data?.token;
    if (!token) return;

    const request: StoreDirectorySupplyMonitorReq = {
      startDate: activeFilters.startDate ?? "",
      endDate: activeFilters.endDate ?? "",
      provinceId: activeFilters.provinceId ?? [],
      cityId: activeFilters.cityId ?? [],
      districtId: activeFilters.districtId ?? [],
      subdistrictId: activeFilters.subdistrictId ?? [],
      roadTypeId: activeFilters.roadTypeId ?? [],
      poiCategory: activeFilters.poiCategory ?? [],
    }
    try {
      const res = await supplyMonitorService.SupplyMonitor(token, request);
      const datas = res.data ?? null
      setData(datas);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAreaData = async () => {
    const token = profile?.data?.token;
    if (!token) return;

    const request: StoreDirectorySupplyMonitorReq = {
      startDate: activeFilters.startDate ?? "",
      endDate: activeFilters.endDate ?? "",
      provinceId: activeFilters.provinceId ?? [],
      cityId: activeFilters.cityId ?? [],
      districtId: activeFilters.districtId ?? [],
      subdistrictId: activeFilters.subdistrictId ?? [],
      roadTypeId: activeFilters.roadTypeId ?? [],
      poiCategory: activeFilters.poiCategory ?? [],
    }
    setLoadingArea(true)
    try {
      const res = await supplyMonitorService.SupplyMonitorAreaList(
        token,
        searchTerm,
        currentPage,
        pageSize,
        areaGroupBy,
        balanceFilter,
        sortBy,
        sortOrder,
        request,
      );
      const areas = res.data ?? []
      setAreaList(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingArea(false)
    }
  };

  const handleOpenModal = (rowAreaId: string, allChildAreaIds: string[]) => {
    setSelectedRepresentativeAreaId(rowAreaId);
    setSelectedAreaIds(allChildAreaIds);
  };

  // ── Simulate fetch (replace with real service call) ──
  useEffect(() => {
    const fetchMain = async () => {
      setLoading(true);
      try {
        await fetchData();
      } finally {
        setLoading(false);
      }
    };
    fetchMain();
  }, [activeFilters]);

  useEffect(() => {
    setCurrentPage(1);
    fetchAreaData();
  }, [currentPage, pageSize, activeFilters, areaGroupBy, balanceFilter, sortBy, sortOrder]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setCurrentPage(1);
    fetchAreaData();
  };

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortOrder("asc");
    }
  };

  const sortIcon = (col: string) => {
    if (sortBy !== col) return null;
    return <i className={`fa-solid fa-sort-${sortOrder === "asc" ? "up" : "down"} ms-1`}></i>;
  };

  const fmt = (n: number) => n.toLocaleString("id-ID");

  const pt = data?.progressTracker;

  return (
    <div className='page-wrapper'>
      <div className='row'>
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Perfect Store - Monitor</h5>
            <div className="d-flex justify-content-end" style={{ flex: 1 }}>
              <>
                <div className='float-end text-decoration-none tm-text-primary'>
                  <button className="btn btn-primary" onClick={() => {
                    console.log("Filter clicked, openFilter:", openFilter);
                    setOpenFilter(true);
                  }}>
                    Filter
                  </button>
                </div>
              </>
            </div>
          </div>
        </div>

        <div className="px-3 pt-3" style={{ maxWidth: 1400, margin: "0 auto" }}>
          {/* ── Stat Cards ── */}
          <div className="row g-3 mb-3">
            <StatCard
              label="Total Stores"
              main={fmt(data?.totalStore?.countedStores ?? 0)}
              sub={fmt(data?.totalStore?.totalStores ?? 0)}
              note="Store(s)"
            />
            <StatCard
              label="Reached Cities"
              main={String(data?.reachedCity?.reachedCities ?? 0)}
              sub={String(data?.reachedCity?.totalCities ?? 0)}
              note="Urban & Rural"
            />
            <StatCard
              label="Manpower"
              main={String(data?.manPower)}
              note="Active in 7 day(s)"
            />
            <StatCard
              label="Area Active"
              main={String(data?.areaActive)}
              note="Active in 7 day(s)"
            />
          </div>

          {/* ── Progress Charts ── */}
          <div className="row g-3 mb-3">
            {/* Acquired Stores — large left */}
            <div className="col-md-7">
              <Placeholder height={320}>
                {loading && <LoadingOverlay />}
                <div className="fw-semibold mb-2" style={{ fontSize: 14 }}>
                  Acquired Stores Progress
                </div>
                <div style={{ height: 270 }}>
                  {pt && <AcquiredStoresChart data={pt.acquiredStore} />}
                </div>
              </Placeholder>
            </div>

            {/* Right column: Active Manpower + Dropped Stores */}
            <div className="col-md-5 d-flex flex-column gap-3">
              <Placeholder height={320}>
                {loading && <LoadingOverlay />}
                <div className="fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Active Manpower Trend
                </div>
                <div style={{ height: 270 }}>
                  {pt && (
                    <TrendChart
                      label="Active Manpower"
                      data={pt.activeManPower}
                      color="#2563EB"
                      fillColor="rgba(37,99,235,0.08)"
                    />
                  )}
                </div>
              </Placeholder>

              {/* <Placeholder height={150}>
                {loading && <LoadingOverlay />}
                <div className="fw-semibold mb-1" style={{ fontSize: 13 }}>
                  Dropped Stores Trend
                </div>
                <div style={{ height: 105 }}>
                  {pt && (
                    <TrendChart
                      label="Dropped Stores"
                      data={pt.droppedStore}
                      color="#EF4444"
                      fillColor="rgba(239,68,68,0.08)"
                    />
                  )}
                </div>
              </Placeholder> */}
            </div>
          </div>

          {/* ── Street Type + Point Interest ── */}
          <div className="row g-3 mb-3">
            <div className="col-md-5">
              <Placeholder height={260}>
                {loading && <LoadingOverlay />}
                <div style={{ height: 230 }} className="d-flex align-items-center">
                  <StreetTypeDoughnut items={data?.streetType ?? []} />
                </div>
              </Placeholder>
            </div>

            <div className="col-md-7">
              <Placeholder height={260}>
                {loading && <LoadingOverlay />}
                <div className="fw-semibold mb-2" style={{ fontSize: 14 }}>
                  Breakdown Per Point Interest
                </div>
                <div style={{ height: 210 }}>
                  <PointInterestBar items={data?.pointInterest ?? []} />
                </div>
              </Placeholder>
            </div>
          </div>

          {/* ── List By Areas ── */}
          <Placeholder height={750}>
            <div className="p-4" style={{ background: 'white' }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="fw-semibold" style={{ fontSize: 24 }}>
                  List By Areas
                </span>

                <form className="d-flex" onSubmit={handleSearchClick}>
                  <input
                    type="text"
                    className="form-control form-control-compact me-2"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: 440 }}
                  />
                  <button type="submit" className="btn btn-primary px-4">
                    <i className="fa-solid fa-search"></i>
                  </button>
                </form>

                <div className="col-12 col-md-auto" style={{ height: 50, maxHeight: 50 }}>
                  <CustomDropdown
                    value={balanceFilter}
                    options={BALANCE_FILTER_OPTION}
                    onChange={(opt) => setBalanceFilter(String(opt.id))}
                    buttonStyle={{ width: 140 }}
                  />
                </div>

                <div className="col-12 col-md-auto" style={{ height: 50, maxHeight: 50 }}>
                  <CustomDropdown
                    value={areaGroupBy}
                    options={AREA_GROUP_BY_OPTIONS}
                    onChange={(opt) => setAreaGroupBy(String(opt.id))}
                    buttonStyle={{ width: 160 }}
                  />
                </div>
              </div>

              <table className="table table-condensed table-hover">
                <thead className="table-primary">
                  <tr>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("province")}>
                      Province {sortIcon("province")}
                    </th>
                    {areaGroupBy !== "province" && (
                      <th style={{ cursor: "pointer" }} onClick={() => handleSort("city")}>
                        City {sortIcon("city")}
                      </th>
                    )}
                    {(areaGroupBy === "subdistrict" || areaGroupBy === "district") && (
                      <th style={{ cursor: "pointer" }} onClick={() => handleSort("district")}>
                        District {sortIcon("district")}
                      </th>
                    )}
                    {areaGroupBy === "subdistrict" && (
                      <th style={{ cursor: "pointer" }} onClick={() => handleSort("subdistrict")}>
                        Subdistrict {sortIcon("subdistrict")}
                      </th>
                    )}
                    <th>Target</th>
                    <th>Current</th>
                    <th>Balance</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    !loadingArea ?
                      areaList.length > 0 ? (
                        areaList.map((row, idx) => {
                          const balanceValue = row.balance?.value ?? 0;
                          const isNeg = balanceValue < 0;
                          const isPos = balanceValue > 0;
                          const balanceColor = isNeg
                            ? "#EF4444"
                            : isPos
                              ? "#F59E0B"
                              : "#22C55E";
                          const balanceLabel = row.balance?.label ?? "0";

                          return (
                            <tr key={idx}>
                              <td>{row.province}</td>
                              {areaGroupBy !== "province" && <td>{row.city}</td>}
                              {(areaGroupBy === "subdistrict" || areaGroupBy === "district") && <td>{row.district}</td>}
                              {areaGroupBy === "subdistrict" && <td>{row.subdistrict}</td>}
                              <td>{row.target}</td>
                              <td>{row.current}</td>
                              <td style={{ color: balanceColor, fontWeight: 500 }}>
                                {balanceLabel}
                              </td>
                              <td>
                                <div className="d-flex">
                                  <button
                                    className="btn btn-outline-info btn-small-compact border-0"
                                    onClick={() => handleOpenModal(row.areaId, row.areaIds)}
                                  >
                                    <i className="fa-solid fa-eye"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={9} className="text-center">
                            Tidak ada data
                          </td>
                        </tr>
                      )
                      :
                      <tr>
                        <td colSpan={9} className="text-center">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                  }
                </tbody>
              </table>
              <div className="d-flex justify-content-end align-items-center mt-3">
                <div className="d-flex align-items-center">
                  <div className="me-4 d-flex align-items-center">
                    <label className="me-2">Rows per page:</label>
                    <CustomDropdown
                      value={pageSize}
                      options={PageSizeOption.options}
                      onChange={(opt) => {
                        setPageSize(Number(opt.id));
                        setCurrentPage(1);
                      }}
                      align="top"
                    />
                  </div>
                  <span className="me-3">
                    {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                  </span>

                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                          <i className="fa fa-angle-double-left"></i>
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                          <i className="fa fa-angle-left"></i>
                        </button>
                      </li>

                      <li className="page-item disabled">
                        <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                      </li>

                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                          <i className="fa fa-angle-right"></i>
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                          <i className="fa fa-angle-double-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </Placeholder>
        </div>
      </div>

      {openFilter && (
        <ModalFilterSupplyMonitor
          onClose={() => setOpenFilter(false)}
          onApply={(filters) => {
            console.log("onApply received:", filters);
            setOpenFilter(false);
            setActiveFilters({
              provinceId: filters.provinceId,
              cityId: filters.cityId,
              districtId: filters.districtId,
              subdistrictId: filters.subdistrictId,
              poiCategory: filters.pointInterest,
              roadTypeId: filters.roadType,
              startDate: filters.startDate,
              endDate: filters.endDate,
            });
          }}
          activeFilters={{
            provinceId: activeFilters.provinceId,
            cityId: activeFilters.cityId,
            districtId: activeFilters.districtId,
            subdistrictId: activeFilters.subdistrictId,
            pointInterest: activeFilters.poiCategory,
            roadType: activeFilters.roadTypeId,
          }}
        />
      )}

      {selectedAreaIds && (
        <AreaMerchantPartnerListModal
          show={!!selectedAreaIds}
          areaIds={selectedAreaIds}
          representativeAreaId={selectedRepresentativeAreaId}
          onClose={() => {
            setSelectedAreaIds(null)
            setSelectedRepresentativeAreaId("");
          }}
        />
      )}
    </div>
  );
};

export default StoreDirectorySupplyMonitor;